import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import Config from '../../utils/config.service';
import AppLanguageService, { AppLanguageOption } from '../../services/app-language.service';

type PickerMode = 'emit' | 'redirect';

@Component({
	standalone: true,
	selector: 'siffi-language-picker',
	templateUrl: './siffi-language-picker.component.html',
	styleUrl: './siffi-language-picker.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule]
})
export class SiffiLanguagePickerComponent implements OnInit, OnChanges {
	constructor(
		private config: Config,
		private appLanguageService: AppLanguageService
	) {}
	@Input() currentLocale: string = 'en_GB';
	@Input() mode: PickerMode = 'emit';
	@Input() syncLocaleQueryParam: boolean = false;
	@Input() disabled: boolean = false;
	@Input() compact: boolean = false;
	@Output() localeSelected: EventEmitter<string> = new EventEmitter<string>();

	@ViewChild('trigger', { static: true }) triggerRef!: ElementRef<HTMLElement>;
	@ViewChild('popup') popupRef?: ElementRef<HTMLElement>;

	isOpen: boolean = false;
	languages: Array<AppLanguageOption> = [];
	currentLanguage!: AppLanguageOption;
	selectableLanguages: Array<AppLanguageOption> = [];
	private hoverCloseTimeout?: ReturnType<typeof setTimeout>;
	private readonly mobileNavMaxWidth: number = 768;

	ngOnInit() {
		this.updateLanguages();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['currentLocale'] || changes['mode']) {
			this.updateLanguages();
		}
	}

	getFlagUrl(flag: string): string {
		return `https://flagcdn.com/w40/${flag}.png`;
	}

	trackByLocale(_: number, language: AppLanguageOption): string {
		return language.locale;
	}

	toggle(event?: Event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		if (this.disabled) {
			return;
		}
		this.isOpen = !this.isOpen;
	}

	open() {
		if (this.disabled || !this.canUseDesktopHover()) {
			return;
		}
		this.cancelHoverClose();
		this.isOpen = true;
	}

	scheduleHoverClose() {
		if (!this.canUseDesktopHover()) {
			return;
		}
		if (this.hoverCloseTimeout) {
			return;
		}
		this.hoverCloseTimeout = setTimeout(() => {
			this.isOpen = false;
			this.hoverCloseTimeout = undefined;
		}, 300);
	}

	cancelHoverClose() {
		if (this.hoverCloseTimeout) {
			clearTimeout(this.hoverCloseTimeout);
			this.hoverCloseTimeout = undefined;
		}
	}

	selectLanguage(language: AppLanguageOption) {
		if (this.disabled) {
			return;
		}
		if (this.mode === 'redirect') {
			window.location.href = this.buildRedirectUrl(language);
			return;
		}
		this.isOpen = false;
		this.localeSelected.emit(language.locale);
	}

	private buildRedirectUrl(language: AppLanguageOption): string {
		const currentUrl = new URL(window.location.href);
		const targetUrl = new URL(
			currentUrl.pathname + currentUrl.search + currentUrl.hash,
			this.config.getSiffiPublicWebUrl(language.locale)
		);
		if (this.syncLocaleQueryParam) {
			targetUrl.searchParams.set('locale', language.locale);
		}
		return targetUrl.toString();
	}

	private getCurrentLanguageByHost(): AppLanguageOption | undefined {
		const currentHost = window.location.hostname;
		return this.languages.find((language) => {
			const languageHost = new URL(this.config.getSiffiPublicWebUrl(language.locale)).hostname;
			return currentHost === languageHost || currentHost.startsWith(language.code + '.');
		});
	}

	private updateLanguages() {
		this.languages = this.appLanguageService.getLanguagesByOrigin('siffi.com');
		this.currentLanguage = this.resolveCurrentLanguage();
		this.selectableLanguages = this.languages.filter((language) => language.locale !== this.currentLanguage.locale);
	}

	private resolveCurrentLanguage(): AppLanguageOption {
		if (this.mode === 'redirect') {
			const byHost = this.getCurrentLanguageByHost();
			if (byHost) {
				return byHost;
			}
		}
		return this.languages.find((language) => language.locale === this.currentLocale) || this.appLanguageService.getLanguage('en_GB');
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent) {
		if (!this.isOpen) {
			return;
		}
		const target = event.target as Node;
		const triggerElement = this.triggerRef?.nativeElement;
		const popupElement = this.popupRef?.nativeElement;
		if (triggerElement?.contains(target) || popupElement?.contains(target)) {
			return;
		}
		this.isOpen = false;
	}

	@HostListener('document:mousemove', ['$event'])
	onDocumentMouseMove(event: MouseEvent) {
		if (!this.isOpen || !this.canUseDesktopHover()) {
			return;
		}
		const target = event.target as Node | null;
		const triggerElement = this.triggerRef?.nativeElement;
		const popupElement = this.popupRef?.nativeElement;
		if (target && (triggerElement?.contains(target) || popupElement?.contains(target))) {
			this.cancelHoverClose();
			return;
		}
		this.scheduleHoverClose();
	}

	@HostListener('window:resize')
	onResize() {
		if (this.canUseDesktopHover()) {
			return;
		}
		this.cancelHoverClose();
	}

	private canUseDesktopHover(): boolean {
		return window.innerWidth > this.mobileNavMaxWidth && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
	}
}
